import { VgEmploymentState } from '@interfaces';
import {
  AccountTypes,
  IndividualDesignationListedTypes,
  InvestmentObjectives,
  TransactionTypesEnum,
  YesNoEmpty,
} from '@interfaces/models';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { TransactionState } from '@store/index';
import { DecedentInfoState } from '@store/reducers/decedent-info/decedent-info.reducer';
import {
  FeatureToggleState,
  getMultiInheritanceEstateToggle,
  getMultiInheritanceToggle,
} from '@store/reducers/feature/feature.reducer';
import { PersonalDataState } from '@store/reducers/personal-data/personal-data.reducer';
import {
  getIsMultiInheritanceFlow,
  getIsTrusteeOrEstate,
  isIneligibleIRADesignation,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { TAX_DOCUMENTATION_REQUIRED_STATES } from '@store/selectors/tax-document-upload/tax-document-upload.helpers';

export const getMultiInheritanceTriageNextPage = (
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
): string => {
  const isInternalCrew = getConfigClientFromRTE().isInternal;
  const ineligibleForClient =
    !isInternalCrew &&
    (!getIsMultiInheritanceFlow(triageState) || !getMultiInheritanceToggle(featureToggleState));
  const ineligibleForCrew = isInternalCrew && !getIsMultiInheritanceFlow(triageState);
  const ineligible = ineligibleForClient || ineligibleForCrew;
  const isTrusteeOrEstate = getIsTrusteeOrEstate(triageState) === YesNoEmpty.YES;
  if (ineligible) return 'error';
  if (isTrusteeOrEstate) return 'multi-inheritance/trust-triage';
  return 'multi-inheritance/summary';
};

export const getMultiInheritanceTrustTriageNextPage = (
  triageState: TriageState,
  featureToggleState: FeatureToggleState,
): string => {
  if (isIneligibleIRADesignation(triageState, featureToggleState)) {
    return 'inheritance/ineligible';
  } else {
    return 'multi-inheritance/summary';
  }
};

export const getMultiInheritanceDecedentInfoRoute = (
  decedentInfoState: DecedentInfoState,
  featureToggleState: FeatureToggleState,
  isReviewPageReachedAndEditMode: boolean,
): string => {
  if (!decedentInfoState.isDeathVerified) return 'secure/multi-inheritance/upload';

  if (isReviewPageReachedAndEditMode) return 'secure/multi-inheritance/review';

  if (getMultiInheritanceEstateToggle(featureToggleState)) return 'secure/multi-inheritance/upload';

  return 'secure/multi-inheritance/account-triage';
};

export const getMultiInheritanceUploadNextPage = (
  isReviewPageReachedAndEditMode: boolean,
  inheritanceWaiverState: string,
  selectL8ItwUploadToggle: boolean,
): string => {
  if (isReviewPageReachedAndEditMode) {
    if (selectL8ItwUploadToggle) {
      if (inheritanceWaiverState === TAX_DOCUMENTATION_REQUIRED_STATES.NJ) {
        return 'secure/multi-inheritance/upload/L8-upload';
      }

      if (inheritanceWaiverState === TAX_DOCUMENTATION_REQUIRED_STATES.PA) {
        return 'secure/multi-inheritance/upload/ITW-upload';
      }
    }
    return 'secure/multi-inheritance/review';
  }
  return 'secure/multi-inheritance/account-triage';
};

export const getMultiInheritanceAccountTriageNextPage = (
  isNewAccount: boolean,
  isMultipleNewAccountTransaction: boolean,
  isReviewPageReachedAndEditMode: boolean,
  isCurrentTransactionFirstNewAccount: boolean,
  currentTransactionInvestmentObjectives: InvestmentObjectives | null,
  isVCDToggleEnabled: boolean,
  isIndividualExecutor: boolean,
  isOnlyExecutor: boolean,
  isOnlyTrustee: boolean,
  isIRASingleExecutor: boolean,
  inheritanceWaiverState: string,
  isPAElligible: boolean,
  selectL8ItwUploadToggle: boolean,
  isExecSpouseFlow: boolean,
  individualExecutorEstateTransactions: TransactionState[],
  iraExecutorEstateTransactions: TransactionState[],
  isL8ITWUploadPageVisited: boolean,
  isContactInfoConfirmed: boolean,
): string | undefined => {
  if (selectL8ItwUploadToggle && !isL8ITWUploadPageVisited) {
    if (inheritanceWaiverState === 'NJ') {
      return 'secure/multi-inheritance/upload/L8-upload';
    }

    if (inheritanceWaiverState === 'PA' && isPAElligible) {
      return 'secure/multi-inheritance/upload/ITW-upload';
    }
  }

  if (isNewAccount) {
    if (
      isIndividualExecutor &&
      isOnlyExecutor &&
      !isExecSpouseFlow &&
      !individualExecutorEstateTransactions.length
    ) {
      return 'secure/multi-inheritance/estate';
    }

    if (isOnlyTrustee) {
      return 'secure/multi-inheritance/trust-info';
    }

    if (isIRASingleExecutor && !isExecSpouseFlow && !iraExecutorEstateTransactions.length) {
      return 'secure/multi-inheritance/estate';
    }
  }

  if (!isNewAccount) {
    return 'secure/multi-inheritance/review';
  }

  if (!isReviewPageReachedAndEditMode) {
    // Skipping the profile pages for a 2nd+ new account assumes they were already
    // completed for the first one. A resumed draft recreates every transaction up
    // front, so the transaction count alone would skip pages nobody filled in this
    // session — and address/phone are MFE-owned, restored only by visiting
    // contact-info, so the OA validate call would go out with them blank.
    return !isMultipleNewAccountTransaction || !isContactInfoConfirmed
      ? 'secure/multi-inheritance/personal-info'
      : getMultiInheritanceVCDBasedNextPage(isVCDToggleEnabled);
  } else {
    if (isCurrentTransactionFirstNewAccount) {
      return 'secure/multi-inheritance/personal-info';
    }

    // When editing a non-first transfer in multiple new account flow, always go to investment objectives
    if (isMultipleNewAccountTransaction) {
      return getMultiInheritanceVCDBasedNextPage(isVCDToggleEnabled);
    }

    if (!isCurrentTransactionFirstNewAccount && !!currentTransactionInvestmentObjectives) {
      return 'secure/multi-inheritance/review';
    }
  }
};

export const getMultiInheritanceVCDBasedNextPage = (isVCDToggleEnabled: boolean) => {
  return isVCDToggleEnabled
    ? 'secure/multi-inheritance/settlement-fund'
    : 'secure/multi-inheritance/investment-objectives';
};

export const getMultiInheritanceEstateNextPage = (isEditingFromReviewPage: boolean): string => {
  return isEditingFromReviewPage
    ? 'secure/multi-inheritance/review'
    : 'secure/multi-inheritance/personal-info';
};

export const getMultiInheritancePersonalInfoNextPage = (): string => {
  return 'secure/multi-inheritance/contact-info';
};

export const getMultiInheritanceContactInfoNextPage = (
  isReviewPageReachedAndEditMode: boolean,
  isOAReviewPageReachedAndEditMode: boolean,
  isCurrentTransactionFirstNewAccount: boolean,
  isEditingProfile: boolean,
  isVCDEnabled: boolean,
): string => {
  let route = isVCDEnabled
    ? 'secure/multi-inheritance/settlement-fund'
    : 'secure/multi-inheritance/employment';

  // editing profile section from review page
  if (isEditingProfile && isReviewPageReachedAndEditMode) {
    route = 'secure/multi-inheritance/review';
  }

  // editing account transfer from review page
  if (!isEditingProfile && isReviewPageReachedAndEditMode) {
    // if NOT first new account, go back to review page
    // if first new account, continue to settlement-fund or employment
    route = !isCurrentTransactionFirstNewAccount ? 'secure/multi-inheritance/review' : route;
  }

  if (isOAReviewPageReachedAndEditMode) {
    route = 'secure/multi-inheritance/new-account-review';
  }

  return route;
};

export const getMultiInheritanceEmploymentNextPage = (
  employment: Partial<VgEmploymentState> | undefined,
  isReviewPageReachedAndEditMode: boolean,
): string => {
  const associated = employment?.associations?.associated ?? false;
  const spouse = employment?.associations?.spouse ?? false;
  const employmentStatus = employment?.employmentStatus?.employmentStatus ?? '';

  if (associated && !spouse && employmentStatus !== 'VE') {
    return 'secure/multi-inheritance/upload/letter';
  }

  if (isReviewPageReachedAndEditMode) {
    return 'secure/multi-inheritance/review';
  }

  return 'secure/multi-inheritance/income-net-worth';
};

export const getMultiInheritanceLetterUploadNexPage = (
  isReviewPageReachedAndEditMode: boolean,
  isOnlyTrustee: boolean,
): string => {
  if (isReviewPageReachedAndEditMode) {
    return 'secure/multi-inheritance/review';
  }
  if (isOnlyTrustee) {
    return 'secure/multi-inheritance/investment-objectives';
  }
  return 'secure/multi-inheritance/income-net-worth';
};

export const getMultiInheritanceIncomeNetWorthNextPage = (
  isReviewPageReachedAndEditMode: boolean,
  isCurrentTransactionFirstNewAccount: boolean,
): string => {
  return isReviewPageReachedAndEditMode && !isCurrentTransactionFirstNewAccount
    ? 'secure/multi-inheritance/review'
    : 'secure/multi-inheritance/investment-objectives';
};

export const getMultiInheritanceInvestObjNextPage = (
  isReviewPageReachedAndEditMode: boolean,
): string => {
  return isReviewPageReachedAndEditMode
    ? 'secure/multi-inheritance/review'
    : 'secure/multi-inheritance/new-account-review';
};

export const getMultiInheritanceNewAccountReviewNextPage = (
  currentTransactionId: string,
): string => {
  return !!currentTransactionId
    ? 'secure/multi-inheritance/review'
    : 'secure/multi-inheritance/account-triage';
};

export const getMultiInheritanceReviewNextPage = (
  transactions: TransactionState[],
  isNewTermsEnabled: boolean,
): string => {
  const isExistingSpouseFlow =
    !!transactions.length &&
    transactions.every(
      (transaction) =>
        transaction.selectedClientAccounts?.transactionType === TransactionTypesEnum.EXISTING,
    );

  if (isExistingSpouseFlow) {
    return 'secure/multi-inheritance/terms/existing';
  }

  if (isNewTermsEnabled) {
    return 'secure/multi-inheritance/terms/review-sign';
  }

  return 'secure/multi-inheritance/terms/multi-new';
};

export const getMultiInheritanceTermsNextPage = (): string => {
  return 'secure/multi-inheritance/confirm';
};

export const getMultiInheritanceSettlementFundNextPage = (
  isMultipleNewAccountTransaction: boolean,
): string => {
  return !isMultipleNewAccountTransaction
    ? 'secure/multi-inheritance/employment'
    : 'secure/multi-inheritance/investment-objectives';
};

export const getMultiInheritanceTrustInfoNextPage = (): string => {
  return 'secure/multi-inheritance/trust-tax-info';
};

export const getMultiInheritanceTrustTaxInfoNextPage = ({
  personalData,
  isReviewPageReachedAndEditMode,
  transactions,
  skipInvestments,
}: {
  personalData: PersonalDataState;
  isReviewPageReachedAndEditMode: boolean;
  transactions: TransactionState[];
  skipInvestments: boolean;
}): string => {
  const skipPersonalData = personalData?.loaded && transactions?.length > 1;
  switch (true) {
    case isReviewPageReachedAndEditMode:
    case skipInvestments:
      return 'secure/multi-inheritance/review';
    case skipPersonalData:
      return 'secure/multi-inheritance/settlement-fund';
    default:
      return 'secure/multi-inheritance/personal-info';
  }
};

export const getMultiInheritanceL8ItwUploadNextPage = (
  currentTransactionType: string,
  isReviewPageReachedAndEditMode: boolean,
  isIndividualExecutor: boolean,
  isIRASingleExecutor: boolean,
  isOnlyTrustee: boolean,
  isOnlyExecutor: boolean,
  individualExecutorEstateTransactions: TransactionState[],
  iraExecutorEstateTransactions: TransactionState[],
  isExecSpouseFlow: boolean,
): string => {
  if (currentTransactionType === TransactionTypesEnum.EXISTING || isReviewPageReachedAndEditMode) {
    return 'secure/multi-inheritance/review';
  } else {
    if (
      !isExecSpouseFlow &&
      ((isIndividualExecutor && isOnlyExecutor && !individualExecutorEstateTransactions.length) ||
        (isIRASingleExecutor && !iraExecutorEstateTransactions.length))
    ) {
      return 'secure/multi-inheritance/estate';
    }

    if (isOnlyTrustee) {
      return 'secure/multi-inheritance/trust-info';
    }

    return 'secure/multi-inheritance/personal-info';
  }
};

export const isPAElligible = (currentTransaction: Partial<TransactionState> | undefined) => {
  return (
    (currentTransaction?.accountTriage?.accountTypeInheriting === AccountTypes.INDV ||
      currentTransaction?.accountTriage?.accountTypeInheriting === AccountTypes.JOINT) &&
    currentTransaction?.accountTriage?.individualDesignation ===
      IndividualDesignationListedTypes.BENEFICIARY &&
    currentTransaction?.accountTriage?.isSpouse === 'NO'
  );
};

export const isNJOrPAEligible = (
  inheritanceWaiverState: string,
  currentTransaction: Partial<TransactionState> | undefined,
) => {
  return (
    inheritanceWaiverState === TAX_DOCUMENTATION_REQUIRED_STATES.NJ ||
    (inheritanceWaiverState === TAX_DOCUMENTATION_REQUIRED_STATES.PA &&
      isPAElligible(currentTransaction))
  );
};
